// src/components/admin/AdminDashboard.tsx
import React, { useState, useEffect } from 'react';
import apiElCristo from '../../service/api_elcristo';

interface AdminDashboardProps {
  user: any;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ user }) => {
  const [stats, setStats] = useState({
    totalSections: 0,
    lastUpdate: '',
    serverStatus: 'checking'
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Cargar contenido
      const content: any = await apiElCristo.getSiteContent();
      
      // Contar secciones
      const sections = Object.keys(content).filter(key => 
        key !== 'last_updated' && key !== 'last_updated_by'
      );
      
      setStats({
        totalSections: sections.length,
        lastUpdate: content.last_updated || 'N/A',
        serverStatus: 'healthy'
      });
    } catch (error) {
      console.error('Error loading dashboard:', error);
      setStats(prev => ({ ...prev, serverStatus: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="elcristo-admin-content">
        <div className="elcristo-loading-spinner">
          <div className="elcristo-spinner"></div>
          <p>Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="elcristo-admin-content">
      <div className="elcristo-content-header">
        <h1>Dashboard</h1>
        <p>Bienvenido, {user?.name || 'Administrador'}</p>
      </div>

      {/* Stats Cards */}
      <div className="elcristo-stats-grid">
        <div className="elcristo-stat-card">
          <div className="elcristo-stat-icon" style={{ backgroundColor: '#667eea' }}>
            <i className="fas fa-layer-group"></i>
          </div>
          <div className="elcristo-stat-content">
            <h3>{stats.totalSections}</h3>
            <p>Secciones Activas</p>
          </div>
        </div>

        <div className="elcristo-stat-card">
          <div className="elcristo-stat-icon" style={{ backgroundColor: '#48bb78' }}>
            <i className="fas fa-server"></i>
          </div>
          <div className="elcristo-stat-content">
            <h3 style={{ 
              color: stats.serverStatus === 'healthy' ? '#48bb78' : '#f56565' 
            }}>
              {stats.serverStatus === 'healthy' ? 'Activo' : 'Error'}
            </h3>
            <p>Estado del Servidor</p>
          </div>
        </div>

        <div className="elcristo-stat-card">
          <div className="elcristo-stat-icon" style={{ backgroundColor: '#ed8936' }}>
            <i className="fas fa-clock"></i>
          </div>
          <div className="elcristo-stat-content">
            <h3 style={{ fontSize: '14px' }}>
              {stats.lastUpdate !== 'N/A' 
                ? new Date(stats.lastUpdate).toLocaleDateString('es-CL')
                : 'N/A'
              }
            </h3>
            <p>Última Actualización</p>
          </div>
        </div>

        <div className="elcristo-stat-card">
          <div className="elcristo-stat-icon" style={{ backgroundColor: '#9f7aea' }}>
            <i className="fas fa-user-shield"></i>
          </div>
          <div className="elcristo-stat-content">
            <h3>{user?.role || 'Admin'}</h3>
            <p>Tu Rol</p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="elcristo-dashboard-section">
        <h2>
          <i className="fas fa-bolt"></i>
          Acciones Rápidas
        </h2>
        <div className="elcristo-quick-actions">
          <a href="/admin/hero" className="elcristo-action-card">
            <i className="fas fa-home"></i>
            <h3>Editar Hero</h3>
            <p>Actualizar página principal</p>
          </a>

          <a href="/admin/news" className="elcristo-action-card">
            <i className="fas fa-newspaper"></i>
            <h3>Gestionar Noticias</h3>
            <p>Agregar o editar noticias</p>
          </a>

          <a href="/admin/team" className="elcristo-action-card">
            <i className="fas fa-users"></i>
            <h3>Equipo Docente</h3>
            <p>Actualizar miembros del equipo</p>
          </a>

          <a href="/admin/users" className="elcristo-action-card">
            <i className="fas fa-user-shield"></i>
            <h3>Usuarios</h3>
            <p>Gestionar accesos</p>
          </a>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="elcristo-dashboard-section">
        <h2>
          <i className="fas fa-history"></i>
          Información del Sistema
        </h2>
        <div className="elcristo-info-grid">
          <div className="elcristo-info-item">
            <strong>Usuario:</strong> {user?.email}
          </div>
          <div className="elcristo-info-item">
            <strong>Rol:</strong> {user?.role}
          </div>
          <div className="elcristo-info-item">
            <strong>Última actualización:</strong> {
              stats.lastUpdate !== 'N/A'
                ? new Date(stats.lastUpdate).toLocaleString('es-CL')
                : 'No disponible'
            }
          </div>
          <div className="elcristo-info-item">
            <strong>API:</strong> backend.anubisai.net/api/elcristo
          </div>
        </div>
      </div>

      {/* Help Section */}
      <div className="elcristo-dashboard-section help-section">
        <h2>
          <i className="fas fa-question-circle"></i>
          ¿Necesitas Ayuda?
        </h2>
        <div className="elcristo-help-content">
          <p>
            Este panel te permite gestionar todo el contenido del sitio web de forma visual.
            Selecciona una sección en el menú lateral para comenzar a editar.
          </p>
          <div className="elcristo-help-links">
            <a href="#" className="elcristo-help-link">
              <i className="fas fa-book"></i>
              Documentación
            </a>
            <a href="#" className="elcristo-help-link">
              <i className="fas fa-video"></i>
              Video Tutoriales
            </a>
            <a href="mailto:soporte@colegioelcristo.cl" className="elcristo-help-link">
              <i className="fas fa-envelope"></i>
              Contactar Soporte
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;